import {
  ROLES_ARVAL,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_PARTNER_READ_ONLY,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  useAuth,
} from 'auth'
import getDisabledChoices from 'lib/getCarDisabledChoices'
import getPictures from 'lib/getCarPictures'
import { useCallback, useMemo, useState } from 'react'

import CarDetails from 'components/organisms/CarDetails'
import CarsToBuyCarDetailsRightBottom from 'components/organisms/CarsToBuy/CarsToBuyCarDetailsRightBottom'

import CarDetailsPageTitle from '../CarDetails/CarDetailsPageTitle'

export default function CarsToBuyCarDetails({
  partnerId,
  plate,
  session,
  isFetching,
  updateChoice,
  setReprice,
  setBlindPrice = () => {},
  blindSessionId,
  toggleFavorite,
  cars,
  isFetchingCars,
  isUpdatingCars,
  selectedCarsToBuy,
  setShowDetails,
  allCars,
  isBlindPublication = false,
  showBlindPrice = false,
  blindReadOnly = false,
  blindOffersRemaining,
}) {
  const { getPartnerId } = useAuth()
  const { hasOneOfRoles } = useAuth()
  const admin = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () =>
      hasOneOfRoles([
        ROLE_READ_ONLY,
        ROLE_LOGISTICS,
        ROLE_B2C,
        ROLE_PARTNER_READ_ONLY,
        ROLE_POST_SALES,
      ]),
    [hasOneOfRoles]
  )

  const myPartnerId = useMemo(
    () => (admin ? partnerId : getPartnerId()),
    [admin, getPartnerId, partnerId]
  )

  const readOnly = useMemo(
    () => isFetchingCars || isFetching || isUpdatingCars,
    [isFetchingCars, isFetching, isUpdatingCars]
  )

  const car = useMemo(() => {
    return (
      plate &&
      cars.find((car) => car.plate.toLowerCase() === plate.toLowerCase())
    )
  }, [plate, cars])

  const pictures = useMemo(
    () => getPictures(car?.pictureurl),
    [car?.pictureurl]
  )

  const caps = useMemo(
    () =>
      (isBlindPublication ? session.partnerBlindCaps : session.partnerCaps) ||
      {},
    [isBlindPublication, session]
  )

  const carsForCaps = useMemo(
    () => (allCars?.length ? allCars : cars),
    [allCars, cars]
  )

  const resolvedBlindOffersRemaining = useMemo(() => {
    if (!isBlindPublication) {
      return blindOffersRemaining ?? null
    }

    const blindCap = Number(caps?.firstChoice ?? 0)
    const usedOffers = carsForCaps.filter(
      (car) => car.blindPrice !== null && car.blindPrice !== undefined
    ).length
    const remaining = blindCap - usedOffers

    return Number.isFinite(remaining) ? Math.max(remaining, 0) : null
  }, [blindOffersRemaining, caps, carsForCaps, isBlindPublication])

  const disabledChoices = useMemo(
    () => getDisabledChoices(carsForCaps, caps),
    [caps, carsForCaps]
  )

  const handleConfirmReprice = (plate, reprice) => {
    setReprice(session.sessionId, myPartnerId, plate, reprice)
  }

  const showNonBlindReprice = !!session.canReprice && !isBlindPublication
  const blindPriceSessionId =
    blindSessionId || (isBlindPublication ? session.sessionId : null)
  const resolvedShowBlindOption = isBlindPublication || showBlindPrice
  const resolvedShowBlindPrice =
    !admin && resolvedShowBlindOption && !!blindPriceSessionId
  const showPriceRange = !isBlindPublication && !resolvedShowBlindPrice

  const handleConfirmBlindPrice = async (plate, reprice) => {
    if (!blindPriceSessionId) {
      return
    }
    await setBlindPrice(blindPriceSessionId, myPartnerId, plate, reprice)
    if (reprice === null || reprice === undefined || reprice === '') {
      clearBlindSelection(plate)
    }
  }

  const [blindSelectionByPlate, setBlindSelectionByPlate] = useState({})

  const getSelection = useCallback(
    (car) => {
      if (car.firstChoice) {
        return 'selected'
      }
      if (car.blindPrice !== null && car.blindPrice !== undefined) {
        return 'blind'
      }
      return blindSelectionByPlate[car.plate] ? 'blind' : 'none'
    },
    [blindSelectionByPlate]
  )

  const clearBlindSelection = useCallback((plate) => {
    setBlindSelectionByPlate((current) => {
      if (!current[plate]) {
        return current
      }
      const next = { ...current }
      delete next[plate]
      return next
    })
  }, [])

  const handleSelectionChange = useCallback(
    async (car, nextSelection) => {
      if (readOnly || userRoleHasReadOnly) {
        return false
      }

      if (nextSelection === 'blind') {
        const hasRemainingBlindOffers =
          resolvedBlindOffersRemaining === null ||
          resolvedBlindOffersRemaining === undefined ||
          Number(resolvedBlindOffersRemaining) > 0
        const hasExistingBlindPrice =
          car.blindPrice !== null &&
          car.blindPrice !== undefined &&
          car.blindPrice !== ''

        if (!hasRemainingBlindOffers && !hasExistingBlindPrice) {
          return false
        }

        if (car.firstChoice) {
          const success = await updateChoice(
            session.sessionId,
            myPartnerId,
            car.plate,
            'none'
          )
          if (!success) {
            return false
          }
        }
        setBlindSelectionByPlate((current) => ({
          ...current,
          [car.plate]: true,
        }))
        return true
      }

      const choice = nextSelection === 'selected' ? 'first' : 'none'
      const success = await updateChoice(
        session.sessionId,
        myPartnerId,
        car.plate,
        choice
      )
      if (!success) {
        return false
      }

      clearBlindSelection(car.plate)
      if (
        resolvedShowBlindPrice &&
        car.blindPrice !== null &&
        car.blindPrice !== undefined
      ) {
        await setBlindPrice(blindPriceSessionId, myPartnerId, car.plate, null)
      }

      return true
    },
    [
      readOnly,
      userRoleHasReadOnly,
      updateChoice,
      session.sessionId,
      myPartnerId,
      resolvedBlindOffersRemaining,
      clearBlindSelection,
      resolvedShowBlindPrice,
      setBlindPrice,
      blindPriceSessionId,
    ]
  )

  const handleToggleFavorite = () => {
    if (car && toggleFavorite) {
      toggleFavorite(session.sessionId, myPartnerId, car.plate, car.favorite)
    }
  }

  return (
    <div className="content-container" data-testid="cars-to-buy-details">
      <>
        <CarDetailsPageTitle
          setShowDetails={setShowDetails}
          cars={cars}
          plate={plate}
        />
        {car && (
          <CarDetails
            car={car}
            price={car.price}
            maxPrice={car.maxPrice}
            showPriceRange={showPriceRange}
            pictures={pictures}
            onToggleFavorite={toggleFavorite ? handleToggleFavorite : undefined}
            rightBottom={
              <CarsToBuyCarDetailsRightBottom
                variant="column"
                session={session}
                partnerId={myPartnerId}
                cars={allCars || cars}
                caps={caps}
                car={car}
                disabledChoices={disabledChoices}
                selectedChoices={
                  session.showOtherPartners
                    ? selectedCarsToBuy?.find(
                        (selectedCar) => selectedCar.plate === car.plate
                      )
                    : undefined
                }
                selection={getSelection(car)}
                onSelectionChange={handleSelectionChange}
                handleConfirmReprice={handleConfirmReprice}
                handleConfirmBlindPrice={handleConfirmBlindPrice}
                showNonBlindReprice={showNonBlindReprice}
                showBlindPrice={resolvedShowBlindPrice}
                readOnly={admin || readOnly || userRoleHasReadOnly}
                showNotice={
                  !['GJ542JB', 'FN144TV', 'FX824EH'].includes(car.plate)
                }
                blindOffersRemaining={resolvedBlindOffersRemaining}
                blindReadOnly={blindReadOnly}
                showBlindOption={resolvedShowBlindOption}
                showBlindConfirmButton={!admin}
              />
            }
          />
        )}
      </>
    </div>
  )
}
